// client/src/components/pages/LexiStar/PlanDispatch/PlanDispatch.tsx
//
// LexiStar plan & dispatch (ST-00188, M2 T6-S06; LS-09 §5.8 and §5.24,
// FR-TRIP-001/003/004/005/021/023). Route /lexistar/plan, page permission
// lexistar.trips.manage; dispatching needs lexistar.trips.dispatch.
//
//  - Planning grid: rows are trips (asset, trailers, driver, route/lane, load,
//    planned start, fuel budget auto / override with reason, channel,
//    validation). Draft rows are added by hand or pasted from a spreadsheet;
//    "Save" → POST /trips:batch (the server computes legs and the litre
//    allowance). Saved PLANNED trips in the window (GET /trips?stage=PLANNED)
//    show as read-only rows.
//  - The driver (ST-00198) is picked from the driver register (GET /drivers): by default the
//    driver allocated to the row's truck at its planned start (GET /trips:resolveDriver, asked
//    whenever the truck or the start changes), else a chosen driver or "No driver"; a pasted
//    name is matched to the register. The channel column is read-only: the driver's register
//    entry decides it (preferred channel, opt-ins).
//  - "Dispatch selected" previews each driver's message per language —
//    rendered by the server from the DRIVER_TRIP_ASSIGNMENT template (M3,
//    POST /message-templates:preview) on the recipient's channel — then
//    POST /trips/:id:dispatch for each.
//  - Run builder with *Next load from here* (POST /runs, /runs/:id:appendTrip).
//  - Unplanned-load prompts (open TRIP.UNPLANNED_LOAD_DETECTED events, live)
//    → retrospective trip (POST /trips:fromDetectedLoad) once a destination
//    is picked (pre-selected when one lane leaves the site).
//
// Converted to TypeScript in ST-00198 (CLAUDE.md rule).

import { useCallback, useEffect, useMemo, useState, type ComponentType } from 'react';
import {
  FaClipboardList,
  FaPlus,
  FaPaste,
  FaSave,
  FaPaperPlane,
  FaTimes,
  FaExclamationTriangle,
} from 'react-icons/fa';
import { PageHeader, UnifiedDateRangePicker, useAuth } from '../../../../types/commonComponents';
import {
  listTrips,
  createTripsBatch,
  getStatusBoard,
  listEvents,
  acknowledgeEvent,
  createTripFromDetectedLoad,
  pickList,
} from '../../../../services/lexistarStatusBoard';
import useLexiStarEvents from '../StatusBoard/useLexiStarEvents';
import {
  ErrorBanner,
  NoticeBanner,
  NoPermissionPage,
  ConnectionIndicator,
  LoadingState,
  cardClasses,
} from '../StatusBoard/atoms';
import PlanGrid from './PlanGrid';
import DispatchPreviewDialog, { type PreviewTrip } from './DispatchPreviewDialog';
import RunBuilder from './RunBuilder';
import UnplannedLoadPromptsJs, { UNPLANNED_LOAD } from './UnplannedLoadPrompts';
import usePlanReference from './usePlanReference';
import useAllocatedDrivers, { allocationKey } from './useAllocatedDrivers';
import useWorkshopEvents from './useWorkshopEvents';
import {
  newDraftRow,
  rowFromTrip,
  parsePastedRows,
  validateRow,
  tripPayload,
  effectiveDriver,
  PASTE_COLUMNS,
} from './planLogic';
import type {
  EffectiveDriver,
  PlanLane,
  PlanRow,
  PlanSite,
  PlanTrip,
  RowValidation,
} from './planTypes';

const DAY_MS = 86400000;

/** An open TRIP.UNPLANNED_LOAD_DETECTED event (GET /events, the live relay). */
interface UnplannedEvent {
  id: string;
  type: string;
  status?: string;
  asset_key?: string;
  event_time?: string;
  payload?: { location_id?: number | string; site_name?: string; ticket_ref?: string | null };
}

// UnplannedLoadPrompts.jsx is JS: its `= []` defaults read as never[] to TypeScript.
const UnplannedLoadPrompts = UnplannedLoadPromptsJs as unknown as ComponentType<{
  events: UnplannedEvent[];
  siteName: (id: unknown) => string | null;
  sites?: PlanSite[];
  lanes?: PlanLane[];
  onCreate: (evt: UnplannedEvent, toLocationId: number) => void;
  onDismiss: (evt: UnplannedEvent) => void;
  busyId: string | null;
  canCreate: boolean;
}>;

interface ApiError {
  response?: { data?: { error?: string; details?: string[]; index?: number } };
  message?: string;
}

const errorOf = (err: unknown): ApiError => (err || {}) as ApiError;

function PasteDialog({
  isOpen,
  onClose,
  onAdd,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (text: string) => void;
}) {
  const [text, setText] = useState('');
  useEffect(() => {
    if (isOpen) setText('');
  }, [isOpen]);
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Paste from spreadsheet"
        className="bg-surface rounded-2xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">Paste from spreadsheet</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600"
          >
            <FaTimes size={18} />
          </button>
        </div>
        <div className="p-6 space-y-3">
          <p className="text-sm text-slate-600">
            Copy rows from Excel or Google Sheets with these columns, in this order (a header row is
            skipped):
          </p>
          <p className="text-xs font-mono text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-2">
            {PASTE_COLUMNS.join(' | ')}
          </p>
          <p className="text-xs text-slate-500">
            The driver is matched to the driver register by tag, then by name; left empty, the
            driver allocated to the truck at the planned start drives. A channel column is ignored:
            messages go on the channel in the driver's register entry.
          </p>
          <label htmlFor="lexistar-plan-paste" className="sr-only">
            Rows to paste
          </label>
          <textarea
            id="lexistar-plan-paste"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={8}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="JX12KLGP	HB45ZNGP	D-01	MA-SB	RB3 coal	PO-1	34	2026-09-24 06:00"
          />
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onAdd(text)}
            disabled={!text.trim()}
            className={`px-5 py-2 text-sm font-semibold text-white rounded-lg shadow-sm ${text.trim() ? 'bg-primary-500 hover:bg-primary-600' : 'bg-slate-400 cursor-not-allowed'}`}
          >
            Add rows
          </button>
        </div>
      </div>
    </div>
  );
}

const PlanDispatch = () => {
  const { hasPermission } = useAuth();
  const canPlan = hasPermission('lexistar.trips.manage');
  const canDispatch = hasPermission('lexistar.trips.dispatch');
  const ref = usePlanReference({ enabled: canPlan, withDrivers: true });

  const [windowStart, setWindowStart] = useState<Date>(() => new Date());
  const [windowEnd, setWindowEnd] = useState<Date>(() => new Date(Date.now() + 2 * DAY_MS));
  const [saved, setSaved] = useState<PlanRow[]>([]);
  const [drafts, setDrafts] = useState<PlanRow[]>(() => [newDraftRow()]);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [preview, setPreview] = useState<PreviewTrip[] | null>(null);
  const [boardRows, setBoardRows] = useState<unknown[]>([]);
  const [prompts, setPrompts] = useState<UnplannedEvent[]>([]);
  const [promptBusy, setPromptBusy] = useState<string | null>(null);

  const fetchSaved = useCallback(async () => {
    if (!canPlan) return;
    setLoading(true);
    try {
      // planned_start is a timestamptz: send the window as SAST-day instants
      // (local midnight of the first day → local midnight after the last).
      const from = new Date(windowStart);
      from.setHours(0, 0, 0, 0);
      const to = new Date(windowEnd);
      to.setHours(24, 0, 0, 0);
      const res = await listTrips({
        stage: 'PLANNED',
        from: from.toISOString(),
        to: to.toISOString(),
        limit: 500,
      });
      setSaved((pickList(res, 'trips') as PlanTrip[]).map(rowFromTrip));
      setError(null);
    } catch (err) {
      setError(errorOf(err).response?.data?.error || 'Failed to load planned trips.');
    } finally {
      setLoading(false);
    }
  }, [canPlan, windowStart, windowEnd]);

  useEffect(() => {
    fetchSaved();
  }, [fetchSaved]);

  useEffect(() => {
    if (!canPlan) return;
    getStatusBoard()
      .then((res: unknown) => setBoardRows(pickList(res, 'rows', 'assets')))
      .catch(() => setBoardRows([]));
    listEvents({ type: UNPLANNED_LOAD, status: 'open' })
      .then((res: unknown) => setPrompts(pickList(res, 'events') as UnplannedEvent[]))
      .catch(() => setPrompts([]));
  }, [canPlan]);

  const connection = useLexiStarEvents({
    enabled: canPlan,
    onEvent: (e: object) => {
      const evt = e as UnplannedEvent;
      if (evt.type === UNPLANNED_LOAD && evt.status !== 'acknowledged') {
        setPrompts((prev) => (prev.some((p) => p.id === evt.id) ? prev : [evt, ...prev]));
      }
    },
  });

  const rows = useMemo(() => [...saved, ...drafts], [saved, drafts]);

  // ST-00198: each row's driver — the allocation of its truck at its planned start unless the
  // planner chose; saved rows as the server decided.
  const allocations = useAllocatedDrivers(drafts, { enabled: canPlan });
  // A pasted name is matched against the register once it has loaded; without it (still loading,
  // or no permission / API) the server matches the name on save.
  const register = ref.loading || ref.failed.includes('drivers') ? undefined : ref.drivers;
  const driverByRow = useMemo(() => {
    const out = new Map<string, EffectiveDriver>();
    for (const r of rows) {
      const key = r.saved ? null : allocationKey(r);
      out.set(
        r.key,
        effectiveDriver(r, {
          driversById: ref.driversById,
          drivers: register,
          allocation: key ? allocations.get(key) || { status: 'loading' } : undefined,
        })
      );
    }
    return out;
  }, [rows, allocations, ref.driversById, register]);
  const driverOf = useCallback(
    (r: PlanRow): EffectiveDriver => driverByRow.get(r.key) || { kind: 'unresolved' },
    [driverByRow]
  );

  // ST-00249: a truck in the workshop (or broken down) at the planned start warns.
  const workshop = useWorkshopEvents(rows, { enabled: canPlan });
  const workshopOf = useCallback((r: PlanRow) => workshop.warningFor(r.asset_id, r.planned_start), [workshop]);

  const validation = useMemo(() => {
    const ctx = {
      assetsById: ref.assetsById,
      routesById: ref.routesById,
      rows,
      now: Date.now(),
      driverOf,
      workshopOf,
    };
    return new Map<string, RowValidation>(rows.map((r) => [r.key, validateRow(r, ctx)]));
  }, [rows, ref.assetsById, ref.routesById, driverOf, workshopOf]);

  const changeDraft = (key: string, patch: Partial<PlanRow>) =>
    setDrafts((prev) =>
      prev.map((r) => (r.key === key ? { ...r, ...patch, row_errors: [], server_warnings: [] } : r))
    );
  const removeDraft = (key: string) => {
    setDrafts((prev) => prev.filter((r) => r.key !== key));
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };
  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const addPasted = (text: string) => {
    const pasted = parsePastedRows(text, { assets: ref.assets, routes: ref.routes });
    const blank = (r: PlanRow) => !r.asset_id && !r.route_id && !r.planned_start && !r.material;
    setDrafts((prev) => [...prev.filter((r) => !blank(r)), ...pasted]);
    setPasteOpen(false);
    const bad = pasted.filter((r) => r.row_errors.length).length;
    setNotice(
      `Added ${pasted.length} row${pasted.length === 1 ? '' : 's'} from the paste${bad ? ` — ${bad} need fixing` : ''}.`
    );
  };

  const saveDrafts = async () => {
    const ready = drafts.filter((r) => validation.get(r.key)?.status !== 'errors');
    const blocked = drafts.length - ready.length;
    if (!ready.length) {
      setError('Nothing to save — fix the rows marked "Fix" first.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const ctx = {
        assetsById: ref.assetsById,
        routesById: ref.routesById,
        lanesById: ref.lanesById,
      };
      // All or nothing on the server: every ready row is created, or none is.
      const res = await createTripsBatch(
        ready.map((r) => tripPayload(r, { ...ctx, driver: driverOf(r) }))
      );
      const newSaved = (pickList(res, 'trips') as PlanTrip[]).map(rowFromTrip);
      setSaved((prev) => [...prev, ...newSaved]);
      setDrafts((prev) => prev.filter((r) => !ready.some((x) => x.key === r.key)));
      setSelected(new Set(newSaved.map((r) => r.key)));
      setNotice(
        `Saved ${newSaved.length} planned trip${newSaved.length === 1 ? '' : 's'}` +
          (blocked ? `; ${blocked} still need fixing` : '') +
          '.'
      );
    } catch (err) {
      const e = errorOf(err);
      const data = e.response?.data || {};
      const message = [data.error, ...(data.details || [])].filter(Boolean).join(' — ');
      const failedRow = Number.isInteger(data.index) ? ready[data.index as number] : undefined;
      if (failedRow) {
        setDrafts((prev) =>
          prev.map((r) =>
            r.key === failedRow.key ? { ...r, row_errors: [message || 'Rejected'] } : r
          )
        );
        setError('Nothing was saved — one row was rejected (marked in the grid).');
      } else {
        setError(message || e.message || 'Saving the plan failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const selectedSaved = saved.filter((r) => selected.has(r.key));
  const selectedDrafts = drafts.filter((r) => selected.has(r.key)).length;

  const openPreview = () => {
    setPreview(
      selectedSaved.map((r) => {
        const route = ref.routesById.get(r.route_id);
        return {
          id: String(r.id),
          reference: r.reference,
          registration: r.registration,
          asset_id: r.asset_id,
          driver_id: r.driver_id,
          driver_name: r.driver_name,
          driver_tag: r.driver_tag,
          driver_source: r.driver_source,
          material: r.material,
          order_ref: r.order_ref,
          from_name: r.from_name,
          to_name: r.to_name,
          route_id: r.route_id,
          route_code: route?.code,
          route_name: route?.name,
          budget_l: r.budget_l,
          planned_km: r.planned_km,
          planned_start: r.planned_start,
        };
      })
    );
  };

  const onDispatched = (ok: PreviewTrip[], failed: Array<{ trip: PreviewTrip; error: string }>) => {
    const done = new Set(ok.map((t) => t.id));
    setSaved((prev) => prev.filter((r) => !done.has(String(r.id))));
    setSelected(new Set());
    if (ok.length) setNotice(`Dispatched ${ok.length} trip${ok.length === 1 ? '' : 's'}.`);
    if (failed.length) {
      setError(
        `${failed.length} trip${failed.length === 1 ? '' : 's'} not dispatched: ${failed.map((f) => `${f.trip.reference} (${f.error})`).join('; ')}`
      );
    }
  };

  const createFromPrompt = async (evt: UnplannedEvent, toLocationId: number) => {
    setPromptBusy(evt.id);
    try {
      const res = await createTripFromDetectedLoad({
        event_id: evt.id,
        asset_key: evt.asset_key,
        from_location_id: evt.payload?.location_id,
        to_location_id: toLocationId,
        detected_at: evt.event_time,
      });
      setPrompts((prev) => prev.filter((p) => p.id !== evt.id));
      setNotice(
        `Created ${res.trip?.reference || 'a trip'} for the detected load — its allowance is marked retrospective.`
      );
    } catch (err) {
      setError(errorOf(err).response?.data?.error || 'Could not create the trip');
    } finally {
      setPromptBusy(null);
    }
  };

  const dismissPrompt = async (evt: UnplannedEvent) => {
    setPromptBusy(evt.id);
    try {
      await acknowledgeEvent(evt.id);
      setPrompts((prev) => prev.filter((p) => p.id !== evt.id));
    } catch (err) {
      setError(errorOf(err).response?.data?.error || 'Could not dismiss the prompt');
    } finally {
      setPromptBusy(null);
    }
  };

  const header = (
    <PageHeader
      icon={FaClipboardList}
      title="Plan & Dispatch"
      subtitle="Plan trips in bulk, check them, dispatch — or chain a truck's next load."
      toolbar={
        canPlan ? (
          <div className="w-full sm:w-64">
            <UnifiedDateRangePicker
              startDate={windowStart}
              endDate={windowEnd}
              onStartDateChange={(d) => d && setWindowStart(d)}
              onEndDateChange={(d) => d && setWindowEnd(d)}
              bounds="all"
            />
          </div>
        ) : null
      }
      actions={canPlan ? <ConnectionIndicator status={connection} /> : null}
    />
  );

  if (!canPlan) {
    return <NoPermissionPage header={header} message="You do not have permission to plan trips." />;
  }

  const missingRef = ref.failed.filter((f) => f !== 'sites');

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 py-6 space-y-6">
        <div>
          <ErrorBanner message={error} onDismiss={() => setError(null)} />
          <NoticeBanner message={notice} onDismiss={() => setNotice(null)} />
          {missingRef.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg flex items-start text-sm">
              <FaExclamationTriangle className="mr-3 mt-0.5 flex-shrink-0" />
              Could not load {missingRef.join(', ')} — those pickers are empty until the API is
              available.
            </div>
          )}
        </div>

        {prompts.length > 0 && (
          <section aria-label="Unplanned load prompts">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Unplanned loads ({prompts.length})
            </h2>
            <UnplannedLoadPrompts
              events={prompts}
              siteName={ref.siteName}
              sites={ref.sites}
              lanes={ref.lanes}
              onCreate={createFromPrompt}
              onDismiss={dismissPrompt}
              busyId={promptBusy}
              canCreate={canPlan}
            />
          </section>
        )}

        <section className={cardClasses} aria-label="Plan">
          <div className="px-6 py-4 border-b border-slate-200 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <p className="text-sm text-slate-600">
              {saved.length} planned · {drafts.length} draft{drafts.length === 1 ? '' : 's'}
              {selected.size > 0 && ` · ${selected.size} selected`}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setDrafts((prev) => [...prev, newDraftRow()])}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                <FaPlus size={11} /> Add row
              </button>
              <button
                type="button"
                onClick={() => setPasteOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                <FaPaste size={11} /> Paste from spreadsheet
              </button>
              <button
                type="button"
                onClick={saveDrafts}
                disabled={saving || drafts.length === 0}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-primary-700 bg-surface border border-primary-400 rounded-lg hover:bg-primary-50 disabled:opacity-50"
              >
                <FaSave size={11} /> {saving ? 'Saving...' : 'Save drafts'}
              </button>
              {canDispatch && (
                <button
                  type="button"
                  onClick={openPreview}
                  disabled={selectedSaved.length === 0}
                  title={selectedDrafts ? 'Drafts are dispatched once saved' : undefined}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg shadow-sm disabled:bg-slate-300 disabled:cursor-not-allowed"
                >
                  <FaPaperPlane size={11} /> Dispatch selected
                  {selectedSaved.length > 0 && ` (${selectedSaved.length})`}
                </button>
              )}
            </div>
          </div>
          {loading && saved.length === 0 && drafts.length === 0 ? (
            <LoadingState label="Loading planned trips..." />
          ) : (
            <PlanGrid
              rows={rows}
              validation={validation}
              drivers={driverByRow}
              driverOptions={ref.drivers}
              selected={selected}
              onToggle={toggle}
              onToggleAll={(on) => setSelected(on ? new Set(rows.map((r) => r.key)) : new Set())}
              onChange={changeDraft}
              onRemove={removeDraft}
              horses={ref.horses}
              trailers={ref.trailers}
              routes={ref.routes}
              lanesById={ref.lanesById}
            />
          )}
          {selectedDrafts > 0 && canDispatch && (
            <p className="px-6 py-3 text-xs text-slate-500 border-t border-slate-100">
              {selectedDrafts} selected draft{selectedDrafts === 1 ? ' is' : 's are'} not saved yet
              — save drafts, then dispatch.
            </p>
          )}
        </section>

        <RunBuilder
          horses={ref.horses}
          sites={ref.sites}
          lanes={ref.lanes}
          boardRows={boardRows}
          prompts={prompts}
          siteName={ref.siteName}
          onCreatePrompt={createFromPrompt}
          onDismissPrompt={dismissPrompt}
          promptBusyId={promptBusy}
          canCreate={canPlan}
          onNotice={setNotice}
        />
      </div>

      <PasteDialog isOpen={pasteOpen} onClose={() => setPasteOpen(false)} onAdd={addPasted} />
      <DispatchPreviewDialog
        isOpen={!!preview}
        trips={preview || []}
        driversById={ref.driversById}
        onClose={() => setPreview(null)}
        onDone={onDispatched}
        workshopWarningFor={(t) => workshop.warningFor(t.asset_id, t.planned_start)}
      />
    </div>
  );
};

export default PlanDispatch;
